import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#F8FDFD] p-6 text-center">
          <div className="bg-white p-8 rounded-3xl shadow-xl shadow-[#006C73]/5 max-w-md w-full border border-gray-100 flex flex-col items-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle size={32} />
            </div>
            <h1 className="text-2xl font-black text-[#003B46] mb-3 tracking-tight">Oops! Something went wrong</h1>
            <p className="text-gray-500 text-sm mb-8">
              We encountered an unexpected error while loading the page. Don't worry, your data is safe.
            </p>
            <button
              onClick={this.handleRefresh}
              className="w-full bg-[#006C73] text-white px-6 py-3.5 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-[#004B57] transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw size={18} />
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
